import { FileUploadStatus, FileWithStatus } from '@/types/upload';
import { getCleanPath } from '@/utils/path';
import { getFileValidationError, isValidFile, UPLOAD_FILES_MAX_QUEUE_SIZE } from '@/utils/validation';
import { useSupportedFileTypes } from '@/hooks/useSupportedFileTypes';
import { useState } from 'react';
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone';

type FileRejectionWithPath = FileRejection & {
  file: FileWithPath;
};

export function useFileDrop({
  onFilesAdded,
  folderId,
}: {
  onFilesAdded: (valid: FileWithStatus[], invalid: FileWithStatus[], folderId?: string) => void;
  folderId?: string;
}) {
  const [resetInputKey, setResetInputKey] = useState(0);
  const { fileTypeOptions } = useSupportedFileTypes();

  const onDrop = (acceptedFiles: FileWithPath[], fileRejections: FileRejectionWithPath[]) => {
    const valid: FileWithStatus[] = [];
    const invalid: FileWithStatus[] = [];

    for (const file of acceptedFiles) {
      const isFileValid = isValidFile(file, fileTypeOptions);

      const fileData: FileWithStatus = {
        id: file.name,
        file,
        name: file.name,
        size: file.size,
        path: getCleanPath(file.path, true) || file.name,
        status: {
          state: isFileValid ? FileUploadStatus.PENDING : FileUploadStatus.FAILED,
          message: isFileValid ? undefined : getFileValidationError(file, fileTypeOptions),
        },
      };

      if (isFileValid) {
        valid.push(fileData);
      } else {
        invalid.push(fileData);
      }
    }

    for (const { file, errors } of fileRejections) {
      invalid.push({
        id: file.name,
        name: file.name,
        size: file.size,
        path: getCleanPath(file.path, true) || file.name,
        file,
        status: {
          state: FileUploadStatus.FAILED,
          code: errors[0]?.code,
          message: errors[0]?.message,
        },
      });
    }
    onFilesAdded(valid, invalid, folderId);
    setResetInputKey(previousKey => previousKey + 1);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: UPLOAD_FILES_MAX_QUEUE_SIZE,
    useFsAccessApi: false,
    noClick: true,
    noKeyboard: true,
    preventDropOnDocument: true,
    multiple: true,
  });

  return {
    dropzoneProps: getRootProps(),
    fileInputProps: {
      ...getInputProps(),
      key: resetInputKey,
    },
    isDragActive,
  };
}
